import type { EnvironmentId } from "@cmmd-forge/contracts";

import type { Project } from "~/types";
import { pullRequestHandoffProjectKey as projectKey } from "./pullRequestHandoffEnvironmentLabels";

/**
 * Every checkout of the pull request's repository the reader could act on.
 *
 * The offered order is a property of the projects, never of the order the store happened to list
 * them in: the source first, because it is what the panel is already showing, then the rest by
 * environment, name, and id. Insertion order is exactly what sent a handoff into an unrelated
 * worktree, so nothing downstream may inherit it.
 */
export function matchingReadyRepositoryProjects(
  source: Project,
  projects: ReadonlyArray<Project>,
  readyEnvironmentIds: ReadonlySet<EnvironmentId>,
): ReadonlyArray<Project> {
  if (!readyEnvironmentIds.has(source.environmentId) || source.availability !== "available")
    return [];
  const canonicalKey = source.repositoryIdentity?.canonicalKey;
  if (canonicalKey === undefined) return [source];
  const matches = projects.filter(
    (project) =>
      readyEnvironmentIds.has(project.environmentId) &&
      project.repositoryIdentity?.canonicalKey === canonicalKey &&
      project.availability === "available",
  );
  const sourceKey = projectKey(source);
  const own = matches.find((project) => projectKey(project) === sourceKey);
  const others = matches
    .filter((project) => projectKey(project) !== sourceKey)
    .toSorted(compareOfferedCheckouts);
  return own === undefined ? others : [own, ...others];
}

/**
 * Total, so the offered order is the same however the projects arrived.
 *
 * Shared with `resolvePullRequestSourceProject`: sorting only the tail of the list would leave
 * its head — the default target, and the one a reader who never opens the picker gets — decided
 * by store insertion order, which is the whole defect.
 */
export function compareOfferedCheckouts(left: Project, right: Project): number {
  if (left.environmentId !== right.environmentId)
    return left.environmentId < right.environmentId ? -1 : 1;
  const byName = left.name.localeCompare(right.name);
  if (byName !== 0) return byName;
  return left.id < right.id ? -1 : left.id > right.id ? 1 : 0;
}

/**
 * The identity an acting override belongs to.
 *
 * Deliberately not the right panel's surface id, which also encodes provider, host, and
 * repository link: those can change for reasons a handoff does not care about, and the override
 * should survive them. Repository case comes from the host and is not identity.
 */
export function pullRequestHandoffKey(input: {
  readonly environmentId: EnvironmentId;
  readonly repository: string;
  readonly number: number;
}): string {
  return `${input.environmentId}:${input.repository.trim().toLowerCase()}#${input.number}`;
}

/**
 * A target the reader picked from "Act in environment". It is an override of the resolved source
 * project, so it belongs to the pull request it was picked for and to nothing else.
 */
export type PullRequestHandoffTargetSelection = {
  readonly pullRequestKey: string;
  readonly targetKey: string;
};

/**
 * The project a handoff acts on: the resolved source, unless the reader overrode it for this
 * pull request.
 *
 * The panel outlives any one pull request — the reader moves between them without it unmounting —
 * and the source moves underneath a single pull request as projects stream in. Keying the
 * override to the pull request is the only pairing that matches what the reader was choosing: it
 * survives the second and dies with the first.
 */
export function resolvePullRequestHandoffTarget(input: {
  readonly pullRequestKey: string;
  readonly sourceProject: Project;
  readonly targets: ReadonlyArray<Project>;
  readonly selection: PullRequestHandoffTargetSelection | null;
}): Project {
  const selection = input.selection;
  if (selection === null || selection.pullRequestKey !== input.pullRequestKey)
    return input.sourceProject;
  return (
    input.targets.find((project) => projectKey(project) === selection.targetKey) ??
    input.sourceProject
  );
}

/**
 * The acting target for a pull request, together with the key that target choice belongs to.
 *
 * Kept as one function so the pairing is testable: the key and the override that is scoped to it
 * are decided together, from the same pull request, rather than in two places that can drift.
 */
export function resolvePullRequestActingTarget(input: {
  /**
   * False beside a thread, where the handoff lands in that thread's composer and the thread is
   * already on one checkout of the branch. Offering a choice there promises something the
   * composer then contradicts, so the panel's own checkout is the answer.
   */
  readonly canPickTarget: boolean;
  readonly environmentId: EnvironmentId;
  readonly detail: { readonly repository: string; readonly number: number };
  readonly sourceProject: Project;
  readonly targets: ReadonlyArray<Project>;
  readonly selection: PullRequestHandoffTargetSelection | null;
}): { readonly pullRequestKey: string; readonly targetProject: Project } {
  const pullRequestKey = pullRequestHandoffKey({
    environmentId: input.environmentId,
    repository: input.detail.repository,
    number: input.detail.number,
  });
  return {
    pullRequestKey,
    targetProject: input.canPickTarget
      ? resolvePullRequestHandoffTarget({
          pullRequestKey,
          sourceProject: input.sourceProject,
          targets: input.targets,
          selection: input.selection,
        })
      : input.sourceProject,
  };
}
